// ADR 23.9.26v §3 — what the page's query cache may leave on disk, and for how
// long, and under which buster.
//
// **An allow-list, by key prefix.** A key not named here — including every key
// added after this ADR — is private until someone adds it, with a reason. Usage,
// billing, share state, another person's shared plan and the anonymous ad
// decision are deliberately absent (§3's second table).
//
// **`CACHE_SCHEMA` is bumped by hand** when a build starts *depending on*
// something an older blob of a persisted key may lack: a field a renderer now
// reads without a fallback (even one the API only just added), a removed or
// retyped field, or a changed query-key shape. The direction matters: the API's
// additive-only habit protects an *old* client reading *new* responses, while a
// restored cache is a *new* build reading *old* ones. Never bump it per deploy —
// that wipes every learner's offline copy on each release, and old-but-readable
// is exactly what offline needs.

import {
  defaultShouldDehydrateQuery,
  type DehydrateOptions,
  type Query,
  type QueryKey,
} from '@tanstack/react-query'

const CACHE_SCHEMA = 1

/**
 * Seven days: Safari's own cap for script-writable storage on a site not added
 * to the home screen, and a fair point for an installed app to stop showing a
 * reading copy without saying how old it is. Also the session snapshot's bound
 * (§2) — a resumed identity with no cache behind it has nothing to show.
 */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/** The persisted prefixes. `['plan']` does not match `['plans']`: parts compare whole. */
export const PERSISTED_PREFIXES: readonly QueryKey[] = [
  ['plans'],
  ['plan'],
  ['flashcard-lists'],
  ['flashcards'],
  ['languages'],
]

function isPersistedKey(key: QueryKey): boolean {
  return PERSISTED_PREFIXES.some((prefix) => prefix.every((part, index) => key[index] === part))
}

/** Only `success` queries under an allowed prefix. */
export function shouldDehydrateQuery(query: Query): boolean {
  return defaultShouldDehydrateQuery(query) && isPersistedKey(query.queryKey)
}

/** Rejects what the running build cannot read, and another user's reads — never what is merely old. */
export function busterFor(userId: string): string {
  return `v${CACHE_SCHEMA}:${userId}`
}

export const dehydrateOptions: DehydrateOptions = {
  shouldDehydrateQuery,
  // Queued writes are W3's, in W3's store.
  shouldDehydrateMutation: () => false,
}
